import { backgroundOrange, gold, mutedGrey, orange } from '@/theme/colors'
import { Button } from '@components/Button'
import { FieldHint, FieldLabel, Input as FieldInput, Textarea as FieldTextarea } from '@components/Field'
import Modal from '@components/Modal'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { fontSizes } from '@/theme/typography'

const DAY_NAME_MAX = 100
const NOTE_MAX = 2000

interface EditDayInfoModalProps {
	isOpen: boolean
	onClose: () => void
	onSave: (name: string | null, note: string | null) => void
	isSaving: boolean
	initialName?: string | null
	initialNote?: string | null
	dayLabel: string
}

const EditDayInfoModal: React.FC<EditDayInfoModalProps> = ({
	isOpen,
	onClose,
	onSave,
	isSaving,
	initialName,
	initialNote,
	dayLabel,
}) => {
	const [name, setName] = useState(initialName || '')
	const [note, setNote] = useState(initialNote || '')

	useEffect(() => {
		if (isOpen) {
			setName(initialName || '')
			setNote(initialNote || '')
		}
	}, [isOpen, initialName, initialNote])

	const handleSave = (e: React.FormEvent) => {
		e.preventDefault()
		onSave(name.trim() || null, note.trim() || null)
	}

	const handleCancel = () => {
		setName(initialName || '')
		setNote(initialNote || '')
		onClose()
	}

	const formId = 'edit-day-info-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title={`Day Info — ${dayLabel}`}
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			size="medium"
			footer={
				<Actions>
					<CancelButton type="button" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</CancelButton>
					<SaveButton type="submit" form={formId} disabled={isSaving}>
						{isSaving ? 'Saving…' : 'Save'}
					</SaveButton>
				</Actions>
			}
		>
			<Form id={formId} onSubmit={handleSave}>
				<FieldGroup>
					<Label htmlFor="day-name">Day Title</Label>
					<SubLabel>Give this day a custom name (e.g. "Beach Day")</SubLabel>
					<Input
						id="day-name"
						type="text"
						value={name}
						onChange={e => setName(e.target.value.slice(0, DAY_NAME_MAX))}
						placeholder="Add a title for this day…"
						maxLength={DAY_NAME_MAX}
					/>
					<CharCount $warn={name.length > DAY_NAME_MAX * 0.9}>
						{name.length} / {DAY_NAME_MAX}
					</CharCount>
				</FieldGroup>

				<FieldGroup>
					<Label htmlFor="day-note">Notes</Label>
					<SubLabel>Add any notes or reminders for this day</SubLabel>
					<Textarea
						id="day-note"
						value={note}
						onChange={e => setNote(e.target.value.slice(0, NOTE_MAX))}
						placeholder="Add a note for this day…"
						rows={6}
						maxLength={NOTE_MAX}
					/>
					<CharCount $warn={note.length > NOTE_MAX * 0.9}>
						{note.length} / {NOTE_MAX}
					</CharCount>
				</FieldGroup>

			</Form>
		</Modal>
	)
}

const Form = styled.form`
	display: flex;
	flex-direction: column;
	gap: 20px;
`

const FieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`

const Label = styled(FieldLabel)``

const SubLabel = styled(FieldHint)`
	margin-bottom: 4px;
`

const Input = styled(FieldInput)``

const Textarea = styled(FieldTextarea)`
	min-height: 120px;
`

const CharCount = styled.span<{ $warn: boolean }>`
	font-size: ${fontSizes.micro};
	color: ${props => (props.$warn ? orange : mutedGrey)};
	text-align: right;
`

const Actions = styled.div`
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	padding-top: 8px;
`

const CancelButton = styled(Button).attrs({ variant: 'secondary' as const })``

/** Day notes keep the gold secondary role. */
const SaveButton = styled(Button)`
	background: ${gold};

	&:hover:not(:disabled) {
		background: ${orange};
	}
`

export default EditDayInfoModal
